<script setup>
import {nl2br} from '@utils/nl2br';
import {preparePictureSrcSets} from '@utils/preparePictureSrcSets';
import PictureComponent from '@images/PictureComponent.vue';
import ButtonComponent from '@buttons/ButtonComponent.vue';
import {imageBreakpoints} from '@utils/imageBreakpoints';
import {computed, inject, markRaw} from 'vue';
import {shortDate} from '@utils/shortDate';

const props = defineProps({
    course: Object || null,
    route: String,
    isAuthenticated: Boolean,
    user: Object || null,
    enrolledCourseIds: Object || null,
});

//Define emits
const emit = defineEmits([
    'open-enroll-modal',
]);

const breakpoints = imageBreakpoints();

const textPrice = computed(() => {
    if(props.course.price === null) {
        return 'Free';
    }
    return sprintf('$%d', props.course.price);
});

const textCountModulesAndSubsections = computed(() => {
    return sprintf('%d x Modules | %d x Lessons', props.course.countModules, props.course.countAllSubsections);
});

const isCourseEnrolled = computed(() => {
    if (!props.enrolledCourseIds || Object.keys(props.enrolledCourseIds).length === 0) {
        return false;
    }
    return props.course.id in props.enrolledCourseIds;
});

const enrolledAt = computed(() => {
    if (!props.enrolledCourseIds || Object.keys(props.enrolledCourseIds).length === 0) {
        return null;
    }
    return props.enrolledCourseIds[props.course.id] || null;
});


const enrolledText = computed(() => {
    if(!isCourseEnrolled.value) {
        return null;
    }
    return 'Enrolled on: ' + shortDate(enrolledAt.value);
});


const enrollCourse = () => {
    //Check if user is logged in
    if(!props.isAuthenticated) {
        window.location.href = '/login';
        return false;
    }
    //Check if this course is free, enroll user
    if(props.course.price === null) {

        return false;
    }
    //Open modal
    emit('open-enroll-modal', {
        course: props.course,
    });

};

</script>

<template>
    <!-- Single Course -->
    <div class="bg-white shadow-lg rounded-lg overflow-hidden border border-blue-light p-2 relative flex flex-col flex-wrap gap-4">
        <div class="pb-[56.25%] flex-none rounded-md overflow-hidden bg-gray-3 relative">
            <div class="absolute top-0 left-0 w-full h-full">
                <div
                    class="absolute top-0 left-0 w-full h-full flex justify-end flex-col flex-wrap z-20 text-white p-4 bg-gradient-to-t from-black/50 via-transparent via-40% to-transparent">
                    <div class="text-4xl font-bold leading-tight" v-text="textPrice"></div>
                    <div class="font-semibold leading-tight" v-text="textCountModulesAndSubsections"></div>
                    <div></div>
                </div>
                <picture-component
                    :alt="course.name"
                    :default-src="course.srcSmall"
                    :sources="preparePictureSrcSets(course.srcSets, breakpoints)"
                    :img-class="'w-full h-full relative group-hover:grayscale transition duration-300 ease-in-out z-10'"
                ></picture-component>
            </div>
        </div>
        <div class="flex flex-col flex-grow flex-wrap p-4 gap-4 lg:gap-6">
            <h3 class="text-2xl font-semibold text-primary-1 leading-tight h-16" v-text="course.name"></h3>
            <div class="flex flex-col flew-wrap gap-4">
                <p v-html="nl2br(course.highlight_text)"></p>
                <div class="flex items-center gap-4">
                    <div>
                        <button-component
                            btn-class="btn-primary-1 !px-7"
                            title="Learn More"
                            :href="route + '/' + course.slug"
                        ></button-component>
                    </div>
                    <div>
                        <button-component
                            v-if="!isCourseEnrolled"
                            @click.prevent.stop="enrollCourse"
                            btn-class="btn-green !px-7"
                            title="Enroll"
                        ></button-component>
                        <div class="text-gray-6 text-sm" v-else v-text="enrolledText"></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>

</style>
